import * as Sentry from '@sentry/react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';

import { artifactDetailQueryOptions } from '@/hooks/queries/artifacts/use-get-artifact';
import { useOrgStore } from '@/stores/org-store';
import type { ArtifactDetailResponse } from '@/types/artifacts';

export interface ArtifactVersionEntry {
  artifactId: string;
  title: string;
  version: number | null;
  editSummary: string | null;
  createdAt: string;
}

function toEntry(detail: ArtifactDetailResponse): ArtifactVersionEntry {
  const meta = detail.artifact_metadata;
  // Workflow-run outputs are promoted without a `version`; a revision with no
  // parent is the lineage root, so it is v1.
  const version = meta?.version ?? (meta?.parent_artifact_id ? null : 1);
  return {
    artifactId: detail.id,
    title: detail.title,
    version,
    editSummary: meta?.edit_summary || null,
    createdAt: detail.created_at,
  };
}

async function walkLineage(
  queryClient: QueryClient,
  orgId: string,
  tip: ArtifactDetailResponse,
): Promise<string[]> {
  const ids = [tip.id];
  const seen = new Set(ids);
  let parentId = tip.artifact_metadata?.parent_artifact_id;

  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    let parent: ArtifactDetailResponse;
    try {
      // A failed stale refetch must not truncate the chain: serve the cached
      // revision and revalidate it in the background.
      parent = await queryClient.ensureQueryData({
        ...artifactDetailQueryOptions(orgId, parentId),
        revalidateIfStale: true,
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'artifact-version-lineage', orgId, parentId },
      });
      break;
    }
    ids.push(parent.id);
    parentId = parent.artifact_metadata?.parent_artifact_id;
  }

  return ids.reverse();
}

function combineEntries(
  results: UseQueryResult<ArtifactDetailResponse, Error>[],
): ArtifactVersionEntry[] | undefined {
  if (results.length === 0 || results.some((r) => !r.data)) return undefined;
  return results.map((r) => toEntry(r.data!));
}

// Revisions are read through the shared detail cache, so switching to one is
// a cache hit and a rename shows up in the lineage.
export const useArtifactVersions = (tip: ArtifactDetailResponse | null) => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const queryClient = useQueryClient();
  const hasParent = !!tip?.artifact_metadata?.parent_artifact_id;

  const { data: lineageIds } = useQuery({
    queryKey: ['artifact-lineage', orgId, tip?.id],
    queryFn: () => walkLineage(queryClient, orgId!, tip!),
    enabled: !!orgId && !!tip && hasParent,
    staleTime: 30_000,
    retry: 1,
  });

  const data = useQueries({
    queries: (lineageIds ?? []).map((id) =>
      artifactDetailQueryOptions(orgId, id),
    ),
    combine: combineEntries,
  });

  return { data };
};
